import React, { useState } from "react";
import { Pie } from "react-chartjs-2";
import { Link } from "react-router-dom";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Helmet } from "react-helmet";
import FAQSchema from "../../FAQSchema";

ChartJS.register(ArcElement, Tooltip, Legend);

const LicSipCalculator = ({ isSip, setIsSip }) => {
  const [monthlyInvestment, setMonthlyInvestment] = useState(25000);
  const [expectedReturnRate, setExpectedReturnRate] = useState(12);
  const [timePeriod, setTimePeriod] = useState(10);

  const calculateSIP = () => {
    const monthlyRate = expectedReturnRate / 100 / 12;
    const months = timePeriod * 12;
    const investedAmount = monthlyInvestment * months;
    const futureValue =
      monthlyInvestment *
      ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) *
      (1 + monthlyRate);
    const estimatedReturns = futureValue - investedAmount;

    return {
      investedAmount,
      estimatedReturns,
      totalValue: futureValue,
    };
  };

  const { investedAmount, estimatedReturns, totalValue } = calculateSIP();

  const data = {
    labels: ["Invested Amount", "Estimated Returns"],
    datasets: [
      {
        label: "SIP Returns Breakdown",
        data: [investedAmount, estimatedReturns],
        backgroundColor: ["#36A2EB", "#FF6384"],
      },
    ],
  };
  function Icon({ id, open }) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        className={`${
          id === open ? "rotate-180" : ""
        } h-5 w-5 transition-transform`}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
        />
      </svg>
    );
  }

  const [activeIndex, setActiveIndex] = useState(0);

  const faqs = [
    {
        question: "Is the LIC SIP Calculator free to use?",
        answer: "Yes, TheFinmaster's LIC SIP Calculator is absolutely free with no hidden charges.",
      },
      {
        question: "Can I use the calculator for lump-sum investments?",
        answer: "No, it is specifically designed for SIP-based calculations.",
      },
      {
        question: "Does the calculator provide guaranteed results?",
        answer: "No. The results are estimates based on expected returns, not guaranteed outcomes.",
      },
      {
        question: "Can I use it for LIC ULIP plans?",
        answer: "No, the calculator is meant only for LIC Mutual Fund SIPs, not insurance-linked plans.",
      },
      {
        question: "What’s the best SIP amount to start with?",
        answer: "Any amount you’re comfortable with. Even ₹500 per month is a great start.",
      },
      {
        question: "What is the ideal tenure for SIP investments?",
        answer: "For best results, a minimum of 5 years is recommended, though 10–15 years yields substantial compounding.",
      },
      {
        question: "Can I change my SIP amount later?",
        answer: "Yes, you can increase or decrease your SIP amount based on your financial flexibility.",
      },
      {
        question: "Are SIP returns taxable?",
        answer: "Yes. Capital gains from mutual funds are subject to taxation based on the fund type and holding period.",
      },
      {
        question: "Do SIPs work well in volatile markets?",
        answer: "Yes. SIPs actually benefit from market volatility through rupee cost averaging.",
      },
      {
        question: "Is KYC mandatory to start SIP in LIC Mutual Funds?",
        answer: "Yes. KYC compliance is mandatory before investing in any mutual fund.",
      },
      {
        question: "Can I stop or pause my SIP anytime?",
        answer: "Yes, SIPs offer flexibility to pause, stop, or resume investments as per your need.",
      },
      {
        question: "Will my SIP returns fluctuate?",
        answer: "Yes. SIP returns are market-linked and may fluctuate over short periods.",
      },
      {
        question: "Can I get a monthly statement of my investments?",
        answer: "Yes, AMC platforms and distributors usually send monthly reports to your email.",
      },
      {
        question: "Is SIP better than Fixed Deposits?",
        answer: "Over the long term, equity SIPs often outperform FDs, but they also carry higher risk.",
      },
      {
        question: "Is SIP suitable for first-time investors?",
        answer: "Absolutely. SIP is one of the best investment options for beginners due to its low-risk, low-barrier entry.",
      }
    ]

  const toggleAccordion = (index) => {
    if (activeIndex === index) {
      // If the clicked question is already expanded, collapse it
      setActiveIndex(null);
    } else {
      // If the clicked question is not expanded, expand it
      setActiveIndex(index);
    }
  };
  return (
    <>
      <Helmet>
        <title>
        LIC SIP Calculator – Plan Systematic Investments for Better Returns
        </title>
        <meta
          name="description"
          content="Discover how your LIC SIP investments can grow over time. Use this smart calculator to stay on track with your financial goals."
        />
      
        <link
          rel="canonical"
          href="https://thefinmaster.com/financial-tools-calculators/lic-bank-sip-calculator"
        />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <FAQSchema faqs={faqs} />
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800 ">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <Link
              to="/financial-tools-calculators"
              className="text-blue-600 hover:text-blue-800"
            >
              Financial Tools Calculators
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500 "> SBI SIP Calculator</span>
          </li>
        </ol>
      </nav>
      <div className="py-6">
        <div className="container mx-auto p-4 shadow-xl rounded-md ">
          <h1 className="text-2xl font-bold mb-4">
          LIC Bank SIP Returns Calculator
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Monthly Investment
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="100000"
                    value={monthlyInvestment}
                    onChange={(e) =>
                      setMonthlyInvestment(Number(e.target.value))
                    }
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">
                    ₹ {monthlyInvestment.toLocaleString()}
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100000"
                  value={monthlyInvestment}
                  onChange={(e) => setMonthlyInvestment(Number(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Expected Return Rate (P.A.)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={expectedReturnRate}
                    onChange={(e) =>
                      setExpectedReturnRate(Number(e.target.value))
                    }
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{expectedReturnRate} %</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="0.1"
                  value={expectedReturnRate}
                  onChange={(e) =>
                    setExpectedReturnRate(Number(e.target.value))
                  }
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Time Period (Years)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={timePeriod}
                    onChange={(e) => setTimePeriod(Number(e.target.value))}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{timePeriod} Yr</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="50"
                  value={timePeriod}
                  onChange={(e) => setTimePeriod(Number(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <button
                onClick={calculateSIP}
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                Calculate
              </button>
            </div>
            <div>
              <div className="mb-4">
                <h2 className="text-xl font-bold">Results</h2>
                <p>Invested Amount: ₹ {investedAmount.toLocaleString()}</p>
                <p>Estimated Returns: ₹ {estimatedReturns.toLocaleString()}</p>
                <p>Total Value: ₹ {totalValue.toLocaleString()}</p>
              </div>
              <div>
                <h2 className="text-xl font-bold">Breakdown</h2>
                <div className="flex justify-center">
                  <Pie
                    data={data}
                    options={{ maintainAspectRatio: false }}
                    width={300}
                    height={300}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6">
      <div className="w-full md:w-2/3 p-4 mx-auto">
      <div className="container mx-auto py-4">
          <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
            What is the LIC SIP Calculator?
          </h1>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            The LIC SIP Calculator is a smart, online financial planning tool that helps investors estimate the future value of their mutual fund investments made through a Systematic Investment Plan (SIP) under the umbrella of LIC Mutual Funds. With just a few inputs such as monthly investment amount, investment duration, and expected rate of return, this calculator provides an approximate corpus one can accumulate over time.
          </p>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            LIC Mutual Funds, backed by the credibility and trust of Life Insurance Corporation of India, offer a wide variety of SIP plans designed for both conservative and growth-focused investors. The SIP calculator simplifies complex return estimations by providing a visual and numerical representation of your potential investment growth—making it easier to set and track financial goals.
          </p>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            Whether you're planning for your child’s education, your retirement, or wealth accumulation, this calculator helps you forecast returns and make data-driven investment decisions.
          </p>
</div>
          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What is SIP and Why Is It Popular?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              SIP (Systematic Investment Plan) is a disciplined method of investing fixed sums in mutual funds at regular intervals—typically monthly or quarterly. Instead of making lump sum investments, SIP enables investors to build a sizeable corpus through smaller, consistent contributions over time.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Why SIPs are so popular:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Power of Compounding:</strong> SIPs allow you to reap the benefits of compound interest, where your returns start earning additional returns.</li>
              <li><strong>Rupee Cost Averaging:</strong> By investing regularly, you buy more units when prices are low and fewer when prices are high—averaging out the cost of units over time.</li>
              <li><strong>Disciplined Investing:</strong> SIPs cultivate the habit of saving and investing regularly, regardless of market conditions.</li>
              <li><strong>Low Entry Barrier:</strong> With options to start as low as ₹500 per month, SIPs are accessible to everyone, including students, salaried individuals, and new investors.</li>
              <li><strong>Flexibility:</strong> You can choose the amount, tenure, fund type, and even pause or stop SIPs anytime.</li>
              <li><strong>No Need for Market Timing:</strong> SIPs eliminate the stress of timing the market, allowing investors to stay invested for the long term with minimal emotional interference.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Because of these reasons, SIPs have emerged as one of the most preferred investment vehicles among retail investors in India.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How Can an LIC SIP Calculator Help You?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The LIC SIP Calculator acts as a virtual financial guide that enables you to plan your investments with clarity and confidence. Here's how it can help:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Goal Planning:</strong> Want to save ₹10 lakhs for your child’s college? Input your monthly SIP amount and expected return, and see how long it will take. You can also reverse-calculate how much you need to invest monthly to meet a target amount.</li>
              <li><strong>Better Budgeting:</strong> Knowing the required monthly SIP amount helps you plan your monthly finances more efficiently.</li>
              <li><strong>Informed Investing:</strong> Compare different investment durations and returns to choose the most effective SIP route for your financial goals.</li>
              <li><strong>Risk-Free Projections:</strong> Since it’s just a simulation tool, you can explore multiple scenarios without any financial risk.</li>
              <li><strong>Real-Time Visuals:</strong> Most calculators display data in both tabular and graphical formats to help you grasp the growth trajectory.</li>
              <li><strong>Saves Time:</strong> Instead of manually calculating the complex compound interest formula, the calculator gives instant, accurate results.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              With LIC’s reliable brand presence and the ease provided by a SIP calculator, even novice investors can confidently step into mutual fund investments.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How Does the LIC Mutual Fund SIP Calculator Work?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The LIC SIP Calculator works based on a standardized compound interest formula tailored to SIPs:
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Formula Used:</strong><br />
              Future Value = P × [(1 + r)^n – 1] × (1 + r) / r
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Where:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>P = SIP amount per installment</li>
              <li>r = periodic rate of return (annual expected return divided by 12)</li>
              <li>n = total number of installments (months)</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Example:</strong><br />
              Let’s say you invest ₹5,000 per month for 10 years with an expected annual return of 12%.<br />
              Monthly rate = 12% ÷ 12 = 1% or 0.01<br />
              Total months = 10 × 12 = 120<br />
              Using the above formula, the calculator will give you the projected corpus at the end of the tenure, which in this case is over ₹11.6 lakhs approximately.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The LIC SIP Calculator simplifies this math into a one-click solution—just plug in the numbers, and get results instantly, along with a graphical overview of your capital growth and returns earned.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How to Use the LIC SIP Calculator Online
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Using the LIC SIP Calculator on TheFinmaster is seamless and beginner-friendly:
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Step-by-Step Guide:</strong>
            </p>
            <ol className="list-decimal pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Enter Monthly SIP Amount: Type in the amount you wish to invest regularly (e.g., ₹1000, ₹5000, etc.).</li>
              <li>Set Investment Duration: Choose how long you plan to invest—1 year to 30+ years.</li>
              <li>Choose Expected Annual Return: Based on the mutual fund type (debt, hybrid, equity), input the estimated annual return percentage (e.g., 8%–15%).</li>
              <li>Click ‘Calculate’: Instantly view the maturity amount, total invested capital, and total earnings.</li>
              <li>Analyze Graphs & Tables: Understand how your money compounds over time using built-in visuals.</li>
            </ol>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Pro Tips:</strong><br />
              Run multiple simulations by changing one variable at a time.<br />
              Use different return rates to reflect conservative, moderate, or aggressive investment strategies.<br />
              Save or screenshot the results for comparison and future planning.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Advantages of Using TheFinmaster LIC SIP Calculator
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              TheFinmaster’s LIC SIP Calculator stands out due to its rich features and user-first design. Here are some of the key benefits:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Highly Accurate:</strong> Uses financial-grade formulas for precise estimates.</li>
              <li><strong>Visual Insights:</strong> Breaks down maturity value into capital invested and wealth gained with clean visuals.</li>
              <li><strong>Free to Use:</strong> No subscription, registration, or fee is required.</li>
              <li><strong>Highly Customizable:</strong> Change SIP amount, time period, and expected returns on the fly to simulate different outcomes.</li>
              <li><strong>Educational Tool:</strong> Great for understanding how compounding works and how small amounts grow significantly over time.</li>
              <li><strong>No Expert Knowledge Needed:</strong> Designed for regular users with zero finance background.</li>
              <li><strong>Helps with LIC Fund Selection:</strong> Allows you to match your SIP amount and goal with a suitable LIC mutual fund.</li>
              <li><strong>Goal-Based Simulation:</strong> Whether it’s retirement, child’s education, or a home purchase—set your goal and let the calculator do the math.</li>
              <li><strong>Time-Efficient:</strong> Saves hours of manual calculations and multiple spreadsheet models.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Using TheFinmaster’s LIC SIP Calculator is not just about number crunching—it’s about planning your financial future smartly and confidently.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Which Factors Influence SIP Earnings?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              SIP returns aren’t fixed—they depend on multiple dynamic factors. Understanding them can help you make better financial decisions:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Investment Duration:</strong> Longer durations allow compounding to work its magic, significantly boosting returns.</li>
              <li><strong>SIP Amount:</strong> The higher the monthly investment, the bigger the end corpus—provided it is sustained consistently.</li>
              <li><strong>Expected Rate of Return:</strong> Depends on the mutual fund type (debt, hybrid, or equity). Equity funds generally offer higher but volatile returns.</li>
              <li><strong>Market Conditions:</strong> Returns vary with market performance, especially in equity funds.</li>
              <li><strong>Expense Ratio:</strong> Every mutual fund charges a management fee. A lower expense ratio means higher returns in your pocket.</li>
              <li><strong>Fund Performance:</strong> Historic performance and fund manager’s expertise influence the overall growth.</li>
              <li><strong>Inflation:</strong> Reduces the real value of your returns. Always account for inflation when planning long-term goals.</li>
              <li><strong>Taxation:</strong> Depending on the type of fund and duration, capital gains tax can reduce your actual returns.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Being aware of these variables helps you stay grounded in your expectations and align your SIP strategy accordingly.
            </p>
          </div>
        </div>

        <div className="w-full md:w-1/3 p-4">
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
              <h2 className="text-lg font-semibold ">Popular Categories</h2>
            </div>
            <div className="p-2 ">
              <ul>
                <Link to="/financial-tools-calculators/swp-calculator">
                  <li className="py-2  hover:bg-slate-100 border-b border-gray-300">
                    SWP Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/mutul-fund-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Mutual Fund Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/sukanya-samriddhi-yojana-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Samriddhi Yojana Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/fd-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    FD Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/rd-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    RD Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/nps-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    NPS Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/hra-exemption-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    HRA Exemption Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/retirement-planning-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Retirement Planning Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    EMI Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/simple-interest-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Simple Interest Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/compound-interest-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Compound Interest Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/nsc-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    NSC Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/step-up-sip-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Step Up Sip Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/income-tax-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Income Tax Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/home-loan-emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Home Loan Emi Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/car-loan-emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Car Loan Emi Calculator
                  </li>
                </Link>
                {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
              </ul>
            </div>
          </div>
          <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
            <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
              <h2 className="text-lg font-semibold">More Categories</h2>
            </div>
            <div className="p-2">
              <ul>
                <Link to="/financial-tools-calculators/gratuity-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Gratuity Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/APY-calculator">
                  <li className="py-2 border- hover:bg-slate-100 border-gray-300">
                    APY Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/cagr-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    CAGR Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/gst-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    GST Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/flat-vs-reducing-rate-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Flat Vs Reducing Rate Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/equity-margin-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Equity Margin Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/inflation-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Inflation Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/salary-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Salary Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/brokerage-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Brokerage Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/tds-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    TDS Calculator
                  </li>
                </Link>
                {/* {moreCategories.map(category => (
          <Link key={category.id} to={`/category/${category.id}`}>
            <li className="py-2 border-b border-gray-300">{category.name}</li>
          </Link>
        ))} */}
              </ul>
            </div>
          </div>
        </div>
      </div>
      {/* FAQ section */}
      <div className="max-w-4xl mx-auto py-6 mt-10 px-2">
        <h1 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">
          Frequently Asked Questions
        </h1>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default LicSipCalculator;
